import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify"; 
import styles from "./CompleteSignup.module.scss";
import { EyeIcon } from "../../../assets/icons";
import { useCompleteUtilAdminRegMutation } from "../../../service/user-service";
import { getErrorMessage } from "../../../utils/error-handler";
import Button from "../../../common/components/xepp/button/Button";
import { useTenant } from "../../../tenant/branding/useTenant";
import { TenantLogo } from "../../../tenant/branding/TenantLogo";

interface CompleteSignupForm {
  password: string;
  confirmPassword: string;
  token: string;
}

export default function CompleteSignup() {
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { logo } = useTenant();
  
  const [completeUtilAdminReg, { isLoading, error }] = useCompleteUtilAdminRegMutation();
  
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    setError
  } = useForm<CompleteSignupForm>({
    mode: 'onChange',
    defaultValues: {
      password: '',
      confirmPassword: '',
      token: token || ''
    }
  });
  
  const password = watch('password');

  useEffect(() => {
    if (!token) {
      toast.error('Invalid or missing token');
      navigate('/login');
    }
  }, [token, navigate]);
  
  const passwordValidation = {
    required: 'Password is required',
    minLength: {
      value: 10,
      message: 'Password must be at least 10 characters long'
    },
    pattern: {
      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      message: 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
    }
  };
  
  const onSubmit = async (data: CompleteSignupForm) => {
    try {
      const payload = {
        verificationCode: data.token,
        password: data.password,
        confirmPassword: data.confirmPassword
      };
      
      const result = await completeUtilAdminReg(payload).unwrap();
      
      toast.success('Registration completed successfully!');
      navigate('/login');
      
    } catch (err: any) {
      toast.error(getErrorMessage(err?.data))
    }
  };

  return (
    <section className={styles.container}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.formCard}>
          <div className={styles.logo}>
            <TenantLogo logo={logo} alt="Logo" style={{ width: 60 }} />
          </div>

          <h1 className={styles.title}>Complete Registration</h1>

          <div className={styles.form}>
            {/* Password Field */}
            <div className={styles.inputGroup}>
              <label className={styles.label} htmlFor="password">
                Enter New Password
              </label>
              <div className={styles.inputWrapper}>
                <input
                  id="password"
                  type={showNewPassword ? "text" : "password"}
                  className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
                  placeholder="••••••••••••"
                  {...register('password', passwordValidation)}
                />
                <button
                  type="button"
                  className={styles.eyeButton}
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                >
                  <EyeIcon />
                </button>
              </div>
              {errors.password && (
                <span className={styles.errorText}>{errors.password.message}</span>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className={styles.inputGroup}>
              <label className={styles.label} htmlFor="confirmPassword">
                Confirm New Password
              </label>
              <div className={styles.inputWrapper}>
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  className={`${styles.input} ${errors.confirmPassword ? styles.inputError : ''}`}
                  placeholder="••••••••••••"
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (value) =>
                      value === password || 'Passwords do not match'
                  })}
                />
                <button
                  type="button"
                  className={styles.eyeButton}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  <EyeIcon />
                </button>
              </div>
              {errors.confirmPassword && (
                <span className={styles.errorText}>{errors.confirmPassword.message}</span>
              )}
            </div>
            
            {/* Hidden token field */}
            <input
              type="hidden"
              {...register('token', { required: 'Token is required' })}
            />
            
          </div>
        </div>
        
        <div style={{ textAlign: 'center', marginTop: 40 }}>
          <Button 
            text={isLoading ? "Submitting..." : "Submit"} 
            variant="secondary" 
            type="submit"
            disabled={!isValid || isLoading}
          />
        </div>
      </form>
    </section>
  );
}